import type { RegistroPermissao, PapelUsuario } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn } from '@pgcustom/design-system';
import { logger } from '../../core/logger.js';
import type { PapeisPermissoesApi } from './papeis-permissoes.api.js';
import type { PermissoesPapeisPermissoes } from './papeis-permissoes.permissoes.js';

const ROTULOS_PAPEL: Record<PapelUsuario, string> = {
  admin: 'Administrador',
  financeiro: 'Financeiro',
  producao: 'Produção',
  parceiros: 'Parceiros',
  marketing: 'Marketing',
  suporte: 'Suporte',
};

const ORDEM_PAPEL: PapelUsuario[] = ['admin', 'financeiro', 'producao', 'parceiros', 'marketing', 'suporte'];

const ROTULOS_ACAO: Record<string, string> = { ler: 'Ler', criar: 'Criar', atualizar: 'Atualizar', excluir: 'Excluir' };

const TOM_ACAO: Record<string, 'destaque' | 'sucesso' | 'alerta' | 'erro'> = {
  ler: 'destaque',
  criar: 'sucesso',
  atualizar: 'alerta',
  excluir: 'erro',
};

const COLUNAS: PgTableColumn[] = [
  { key: 'recurso', label: 'Recurso' },
  { key: 'acoes', label: 'Ações' },
];

/**
 * `<erp-papeis-permissoes-page>` — visão somente-leitura do RBAC real
 * (`GET /api/v1/auth/permissoes`, dado seedado nas migrations `004`,
 * `006`, `019`, `021` — nunca hardcoded aqui). Recurso `auth`, só
 * `admin`. Sem edição: alterar RBAC continua sendo decisão de
 * arquitetura via migration, não uma ação de UI (mesmo critério que
 * impediu esta sessão de "corrigir" RBAC diretamente no banco).
 */
export class ErpPapeisPermissoesPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _api: PapeisPermissoesApi | null = null;
  private _permissoes: PermissoesPapeisPermissoes = { ler: false };
  private resultado: Resultado<RegistroPermissao[]> = { status: 'carregando' };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set papeisPermissoesApi(valor: PapeisPermissoesApi) {
    this._api = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesPapeisPermissoes) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._api) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0 0 var(--pg-space-1, 4px); }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-bottom: var(--pg-space-5, 24px); }
        .grade { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: var(--pg-space-4, 16px); }
      </style>
      <h1>Papéis e Permissões</h1>
      <div class="subtitulo">Somente leitura — RBAC definido por migration, não editável aqui</div>
      <div class="grade" id="grade"></div>
    `;
    this.atualizar();
  }

  private async carregar(): Promise<void> {
    const api = this._api;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizar();
    try {
      this.resultado = await api.listar();
    } catch (erro) {
      logger.erro('falha ao carregar permissões', { erro });
      this.resultado = { status: 'erro', mensagem: 'falha ao carregar' };
    }
    this.atualizar();
  }

  private atualizar(): void {
    const grade = this.root.querySelector('#grade');
    if (!grade) return;

    if (this.resultado.status !== 'ok') {
      const { variant, titulo, descricao } = estadoVazio(this.resultado, 'Papéis e Permissões', 'nenhuma permissão seedada');
      grade.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
      return;
    }

    const porPapel = new Map<PapelUsuario, RegistroPermissao[]>();
    for (const registro of this.resultado.dado) {
      const lista = porPapel.get(registro.papel) ?? [];
      lista.push(registro);
      porPapel.set(registro.papel, lista);
    }

    grade.innerHTML = ORDEM_PAPEL.map(
      (papel) => `
        <pg-card padding="compacto">
          <span slot="header">${escaparHtml(ROTULOS_PAPEL[papel])}</span>
          <div id="tabela-${papel}"></div>
        </pg-card>`
    ).join('');

    for (const papel of ORDEM_PAPEL) {
      const container = this.root.querySelector(`#tabela-${papel}`);
      if (!container) continue;
      const registros = porPapel.get(papel) ?? [];
      if (registros.length === 0) {
        container.innerHTML = `<pg-empty-state variant="vazio" titulo="${escaparHtml(ROTULOS_PAPEL[papel])}" descricao="nenhum recurso seedado"></pg-empty-state>`;
        continue;
      }
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = registros.map((r) =>
        r.recurso === '*'
          ? {
              recurso: { texto: 'Acesso total (todos os recursos)', tom: 'destaque' as const },
              acoes: { badges: r.acoes.map((a) => ({ texto: ROTULOS_ACAO[a] ?? a, tom: TOM_ACAO[a] ?? ('neutro' as const) })) },
            }
          : {
              recurso: r.recurso,
              acoes: { badges: r.acoes.map((a) => ({ texto: ROTULOS_ACAO[a] ?? a, tom: TOM_ACAO[a] ?? ('neutro' as const) })) },
            }
      );
    }
  }
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum registro', descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-papeis-permissoes-page', ErpPapeisPermissoesPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-papeis-permissoes-page': ErpPapeisPermissoesPage;
  }
}
